import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";

import { Loader } from "@mantine/core";

const ApiReferencePage = lazy(
  () => import("../components/Docs/ApiReferencePage"),
);

// AUTH PAGES
import HealthPage from "../pages/HealthPage";
import LoginPage from "../pages/auth/LoginPage";
import VerifyPinPage from "../pages/auth/VerifyPinPage";
import SetupPinPage from "../pages/auth/SetupPinPage";
import RequestPinReset from "../pages/auth/RequestPinReset";
import ResetPin from "../pages/auth/ResetPin";
import NewPassword from "../pages/auth/NewPassword";
import RequestPasswordReset from "../pages/auth/RequestPasswordReset";
import ResetPassword from "../pages/auth/ResetPassword";
import OTPPage from "../pages/auth/OTPPage";

// DASHBOARD PAGES
import { Layout } from "../components/Layout";
import Dashboard from "../pages/Dashboard";
import Transaction from "../pages/Transaction";
import FundsTransfer from "../pages/FundsTransfer";
import CompanyDetails from "../pages/CompanyDetails";
import MyAccount from "../pages/MyAccount";
import UserRole from "../pages/UserRole";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import TermsAndConditions from "../pages/TermsConditions";
import Unauthorized from "../pages/Unauthorized";
import ProtectedRoute from "../components/ProtectedRoute";
import RBACManagement from "../pages/RBACManagement";
import ManageMerchants from "../pages/ManageMerchants";
import LipaBidhaa from "../pages/LipaBidhaa";
import Paybill from "../pages/Paybill";
import TillNumber from "../pages/TillNumber";
import UnauthorizedPage from "../components/ui/CustomUnauthorized/CustomUnauthorized";
import RequestReactivation from "../pages/auth/RequestReactivation";
import TokenValidatorGuard from "../components/auth/TokenValidatorGuard/TokenValidatorGuard";
import {
  ACTIVATION_TOKEN_ERROR_ROUTES,
  ACTIVATION_TOKEN_FALLBACK_ROUTE,
  PIN_RESET_TOKEN_ERROR_ROUTES,
  PIN_RESET_TOKEN_FALLBACK_ROUTE,
} from "../components/auth/config/tokenGuardRoutes";
import AppAccessGuard from "../components/auth/AppAccessGuard/AppAccessGuard";
import AppInitializingGuard from "../components/auth/AppInitializingGuard/AppInitializingGuard";
import PinLockGuard from "../components/shared/PinLockGuard";
import Paykit from "../pages/Paykit";
import LipaBidhaaFlowGuard from "../components/LipaBidhaa/LipaBidhaaFlowGuard";
import FundsTransferFlowGuard from "../components/FundsTransfer/FundsTransferFlowGuard";
import FundsTransferMobileMoney from "../pages/FundsTransferMobileMoney";
import FundsTransferBank from "../pages/FundsTransferBank";
import Developer from "../pages/Developer";
import FeatureFlagRoute from "../components/FeatureFlagRoute";
import BulkUploads from "../pages/BulkUploads";
import BatchSummary from "../pages/BatchSummary";
import BatchSummaryFlowGuard from "../components/BulkUploads/BatchSummaryFlowGuard";
import ManualEntry from "../pages/ManualEntry";
import FileUpload from "../pages/FileUpload";
import RouteErrorPage from "../components/ui/ErrorBoundary/RouteErrorPage";

const SUPER_ADMIN = "ROLE_SUPER_ADMIN";
const MERCHANT_ADMIN = "ROLE_MERCHANT_ADMIN";
const MERCHANT_AGENT = "ROLE_MERCHANT_AGENT";
const MERCHANT_USER = "ROLE_MERCHANT_USER";

const STANDARD_ROLES = [SUPER_ADMIN, MERCHANT_ADMIN, MERCHANT_USER];
const ALL_ROLES = [SUPER_ADMIN, MERCHANT_ADMIN, MERCHANT_AGENT, MERCHANT_USER];

const router = createBrowserRouter([
  { path: "auth/login", element: <LoginPage /> },
  {
    element: (
      <AppAccessGuard>
        <Outlet />
      </AppAccessGuard>
    ),
    children: [
      { path: "/health", element: <HealthPage /> },
      { path: "auth/verify-pin", element: <VerifyPinPage /> },
      { path: "auth/setup-pin", element: <SetupPinPage /> },
      { path: "auth/forgot-pin", element: <RequestPinReset /> },
      {
        path: "auth/reset-pin",
        element: (
          <TokenValidatorGuard
            errorRoutes={PIN_RESET_TOKEN_ERROR_ROUTES}
            fallbackRoute={PIN_RESET_TOKEN_FALLBACK_ROUTE}
          >
            <ResetPin />
          </TokenValidatorGuard>
        ),
      },
      {
        path: "auth/newpassword",
        element: (
          <TokenValidatorGuard
            errorRoutes={ACTIVATION_TOKEN_ERROR_ROUTES}
            fallbackRoute={ACTIVATION_TOKEN_FALLBACK_ROUTE}
          >
            <NewPassword />
          </TokenValidatorGuard>
        ),
      },
      {
        path: "auth/request-password-reset",
        element: <RequestPasswordReset />,
      },
      { path: "auth/request-reactivation", element: <RequestReactivation /> },
      { path: "auth/reset-password", element: <ResetPassword /> },
      { path: "auth/otp", element: <OTPPage /> },
      { path: "/privacy-policy", element: <PrivacyPolicy /> },
      { path: "/terms-and-conditions", element: <TermsAndConditions /> },
      {
        path: "/",
        element: (
          <AppInitializingGuard>
            <Layout />
          </AppInitializingGuard>
        ),
        errorElement: <RouteErrorPage />,
        children: [
          { index: true, element: <Navigate to="/auth/login" replace /> },
          {
            path: "transaction-page",
            element: (
              <ProtectedRoute allowedRoles={STANDARD_ROLES}>
                <Transaction />
              </ProtectedRoute>
            ),
          },
          {
            path: "funds-transfer",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <PinLockGuard>
                  <FundsTransfer />
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "company-details",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN, MERCHANT_USER]}>
                <CompanyDetails />
              </ProtectedRoute>
            ),
          },
          {
            path: "my-account",
            element: (
              <ProtectedRoute allowedRoles={ALL_ROLES}>
                <PinLockGuard>
                  <MyAccount />
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "user-role",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <UserRole />
              </ProtectedRoute>
            ),
          },
          {
            path: "dashboard",
            element: (
              <ProtectedRoute allowedRoles={STANDARD_ROLES}>
                <Dashboard />
              </ProtectedRoute>
            ),
          },
          {
            path: "manage-roles",
            element: (
              <ProtectedRoute allowedRoles={[SUPER_ADMIN]}>
                <RBACManagement />
              </ProtectedRoute>
            ),
          },
          {
            path: "manage-merchants",
            element: (
              <ProtectedRoute allowedRoles={[SUPER_ADMIN]}>
                <ManageMerchants />
              </ProtectedRoute>
            ),
          },
          {
            path: "lipa-bidhaa",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <PinLockGuard>
                  <LipaBidhaa />
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "lipa-bidhaa/paybill",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <PinLockGuard redirectOnLock="/lipa-bidhaa">
                  <LipaBidhaaFlowGuard>
                    <Paybill />
                  </LipaBidhaaFlowGuard>
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "lipa-bidhaa/till-number",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <PinLockGuard redirectOnLock="/lipa-bidhaa">
                  <LipaBidhaaFlowGuard>
                    <TillNumber />
                  </LipaBidhaaFlowGuard>
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "lipa-bidhaa/paykit",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <PinLockGuard redirectOnLock="/lipa-bidhaa">
                  <LipaBidhaaFlowGuard>
                    <Paykit />
                  </LipaBidhaaFlowGuard>
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "funds-transfer/mobile-money",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <PinLockGuard redirectOnLock="/funds-transfer">
                  <FundsTransferFlowGuard>
                    <FundsTransferMobileMoney />
                  </FundsTransferFlowGuard>
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "funds-transfer/bank",
            element: (
              <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                <PinLockGuard redirectOnLock="/funds-transfer">
                  <FundsTransferFlowGuard>
                    <FundsTransferBank />
                  </FundsTransferFlowGuard>
                </PinLockGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: "bulkpayments",
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute allowedRoles={[MERCHANT_ADMIN, SUPER_ADMIN]}>
                    <BulkUploads />
                  </ProtectedRoute>
                ),
              },
              {
                path: "batch-summary",
                element: (
                  <ProtectedRoute allowedRoles={[MERCHANT_ADMIN, SUPER_ADMIN]}>
                    <BatchSummaryFlowGuard>
                      <BatchSummary />
                    </BatchSummaryFlowGuard>
                  </ProtectedRoute>
                ),
              },
              {
                path: "manual-entry",
                element: (
                  <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                    <ManualEntry />
                  </ProtectedRoute>
                ),
              },
              {
                path: "file-upload",
                element: (
                  <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                    <FileUpload />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "developer-apis",
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                    <FeatureFlagRoute flag="developer">
                      <Developer />
                    </FeatureFlagRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: "docs",
                element: (
                  <ProtectedRoute allowedRoles={[MERCHANT_ADMIN]}>
                    <FeatureFlagRoute flag="developer">
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center h-full">
                            <Loader size="md" />
                          </div>
                        }
                      >
                        <ApiReferencePage />
                      </Suspense>
                    </FeatureFlagRoute>
                  </ProtectedRoute>
                ),
              },
            ],
          },
          { path: "unauthorized", element: <Unauthorized /> },
          {
            path: "*",
            element: (
              <div className="w-full">
                <UnauthorizedPage
                  leftTitle="Page Not Found"
                  leftMessage="The page you are looking for does not exist."
                  rightTitle="404 Error"
                  rightMessage="You may have mistyped the URL or the page may have been removed."
                  actions={[{ label: "Go to Dashboard", href: "/dashboard" }]}
                />
              </div>
            ),
          },
        ],
      },
    ],
  },
]);

export default router;
